import React from 'react';
import { Check, X, Package, Map as MapIcon, Loader2 } from 'lucide-react';
import { PartTag, FeatureKey, PositionKey, circled, tagId, filterId } from '../../../utils/partTags';
import { DIAGRAM_PALETTE } from '../../../utils/diagramTag';

type Tr = (key: string, def: string, vars?: Record<string, unknown>) => string;

// ── Tags on a part card + the filter chips built from them ──────────────────
// On a card each type has its own look: functionality green / light red,
// answer dashed, features lilac, position neutral, kit lilac with a box.
// As FILTERS every tag shares one look (only on / off differs), and a red
// functionality tag is never offered as one (see filterTags).

export const FEATURE_LABEL: Record<FeatureKey, [string, string]> = {
  assembly: ['chat.clarification.tag.assembly', 'Complete assembly'],
  kitPart: ['chat.clarification.tag.kitPart', 'Kit part'],
  keyless: ['chat.clarification.tag.keyless', 'Keyless'],
  highPressure: ['chat.clarification.tag.highPressure', 'High pressure'],
  heated: ['chat.clarification.tag.heated', 'Heated'],
  folding: ['chat.clarification.tag.folding', 'Folding'],
};
export const POSITION_TAG_LABEL: Record<PositionKey, [string, string]> = {
  left: ['chat.clarification.tag.left', 'Left'],
  right: ['chat.clarification.tag.right', 'Right'],
  bothSides: ['chat.clarification.tag.bothSides', 'Both sides'],
  front: ['chat.clarification.tag.front', 'Front'],
  rear: ['chat.clarification.tag.rear', 'Rear'],
  upper: ['chat.clarification.tag.upper', 'Upper'],
  lower: ['chat.clarification.tag.lower', 'Lower'],
  outer: ['chat.clarification.tag.outer', 'Outer'],
  inner: ['chat.clarification.tag.inner', 'Inner'],
};

/** The words a tag shows (the filter chip shows the same, minus "Includes"). */
export const tagText = (t: PartTag, tr: Tr, asFilter = false): string => {
  switch (t.kind) {
    case 'function':
      return t.includes && !asFilter ? tr('chat.clarification.tag.includes', 'Includes {{type}}', { type: t.value.toLowerCase() }) : t.value;
    case 'answer': return `${circled(t.n)} ${t.value}`;
    case 'feature': {
      // An AI value "Position: Center Upper" shows its value alone; the
      // dimension it answers is the chip's tooltip (see TagChip).
      if (t.value) { const at = t.value.indexOf(':'); return at > 0 && at < t.value.length - 1 ? t.value.slice(at + 1).trim() : t.value; }
      const label = FEATURE_LABEL[t.key as FeatureKey];
      return label ? tr(label[0], label[1]) : t.key;
    }
    case 'position': { const [k, d] = POSITION_TAG_LABEL[t.key]; return tr(k, d); }
    case 'kit': return asFilter || !t.n
      ? tr('chat.clarification.tag.kit', 'Kit')
      : tr('chat.clarification.tag.kitParts', 'Kit · {{count}} parts', { count: String(t.n) });
    default: return '';
  }
};

const chipBase: React.CSSProperties = {
  display: 'inline-flex', alignItems: 'center', gap: 3, padding: '1px 7px', borderRadius: 999,
  fontSize: 11, fontWeight: 600, lineHeight: '17px', whiteSpace: 'nowrap', maxWidth: '100%',
  overflow: 'hidden', textOverflow: 'ellipsis', boxSizing: 'border-box', border: '1px solid transparent',
};

export const tagStyle = (t: PartTag): React.CSSProperties => {
  switch (t.kind) {
    case 'function':
      return t.match
        ? { ...chipBase, background: DIAGRAM_PALETTE.successBg, color: DIAGRAM_PALETTE.successFg }
        : { ...chipBase, background: DIAGRAM_PALETTE.dangerBg, color: DIAGRAM_PALETTE.dangerFg };
    case 'answer':
      return { ...chipBase, background: 'transparent', color: DIAGRAM_PALETTE.indigo, border: `1px dashed ${DIAGRAM_PALETTE.indigo}` };
    // Every descriptive tag — a feature, an AI value, a position, the kit —
    // wears ONE look; only the functionality tag (green / red) and the
    // numbered answer say something different and look different.
    case 'feature':
    case 'kit':
    case 'position':
      return { ...chipBase, background: DIAGRAM_PALETTE.bgSubtle, color: '#5F5C57', border: `1px solid ${DIAGRAM_PALETTE.border}` };
    default:
      return chipBase;
  }
};

export const TagChip: React.FC<{ tag: PartTag; tr: Tr; requestedType?: string | null }> = ({ tag, tr, requestedType }) => {
  const text = tagText(tag, tr);
  // The red tag names what the part IS; its tooltip says what it isn't.
  const title = tag.kind === 'function' && !tag.match && requestedType
    ? tr('chat.clarification.tag.notA', 'Not a {{type}}', { type: requestedType.toLowerCase() })
    : tag.kind === 'feature' && tag.value ? tag.value : text;
  return (
    <span data-testid="part-tag" data-kind={tag.kind} data-id={tagId(tag)} data-match={tag.kind === 'function' ? String(tag.match) : undefined}
      style={tagStyle(tag)} title={title}>
      {tag.kind === 'function' && (tag.match ? <Check size={11} strokeWidth={3} aria-hidden /> : <X size={11} strokeWidth={3} aria-hidden />)}
      {tag.kind === 'kit' && <Package size={11} aria-hidden />}
      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{text}</span>
    </span>
  );
};

/** A tag that is a fact for the filters but says nothing to a reader:
 *  "Both sides" (the part fits either side — it answers Left and Right, and
 *  shows neither word). */
export const isSilentTag = (t: PartTag): boolean => t.kind === 'position' && t.key === 'bothSides';

/** ONE row of tags on a card. */
const PartTagRow: React.FC<{ tags: PartTag[]; tr: Tr; requestedType?: string | null; testId?: string; style?: React.CSSProperties }> = ({ tags, tr, requestedType, testId = 'part-tags', style }) => {
  const shown = tags.filter(t => !isSilentTag(t));
  if (!shown.length) return null;
  return (
    <span data-testid={testId} style={{ display: 'flex', flexWrap: 'wrap', gap: 4, minWidth: 0, marginTop: 3, ...style }}>
      {shown.map(t => <TagChip key={tagId(t)} tag={t} tr={tr} requestedType={requestedType} />)}
    </span>
  );
};

/** The filter chips over the list: one look for every tag type. When the
 *  question spans several diagrams (front and rear door panels) they lead
 *  the row, marked with a map icon: picking one switches the drawing. */
export const FilterChips: React.FC<{
  tags: PartTag[];
  /** Parts each chip would leave (by filter id). Absent = no counts shown. */
  counts?: Map<string, number> | null;
  active: Set<string>;
  onToggle: (id: string) => void;
  onClear: () => void;
  tr: Tr;
  /** `label: null` = the name is not final yet (a spinner holds its place). */
  sections?: { key: string; label: string | null; count?: number }[] | null;
  activeSection?: string | null;
  onSelectSection?: (key: string) => void;
}> = ({ tags, counts, active, onToggle, onClear, tr, sections, activeSection, onSelectSection }) => {
  const diagrams = sections && sections.length > 1 && onSelectSection ? sections : [];
  if (!tags.length && !diagrams.length) return null;
  const chipStyle = (on: boolean): React.CSSProperties => ({
    display: 'inline-flex', alignItems: 'center', gap: 4, padding: '3px 10px', borderRadius: 999,
    fontSize: 11.5, fontWeight: 600, lineHeight: '16px', whiteSpace: 'nowrap', cursor: 'pointer', fontFamily: 'inherit',
    border: `1px solid ${on ? DIAGRAM_PALETTE.navy : DIAGRAM_PALETTE.border}`,
    background: on ? DIAGRAM_PALETTE.navy : DIAGRAM_PALETTE.bgSurface,
    color: on ? '#fff' : DIAGRAM_PALETTE.textPrimary,
  });
  return (
    <div data-testid="filter-chips" role="group" aria-label={tr('chat.clarification.filterBy', 'Filter by')}
      style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 5, padding: '8px 12px', borderBottom: `0.5px solid ${DIAGRAM_PALETTE.border}`, background: DIAGRAM_PALETTE.bgSurface }}>
      {diagrams.map(d => {
        const on = d.key === activeSection;
        return (
          <button key={`diagram:${d.key}`} type="button" data-testid="diagram-filter-chip" data-key={d.key} aria-pressed={on}
            title={tr('chat.clarification.showDiagram', 'Show this diagram')}
            onClick={(e) => { e.stopPropagation(); if (!on) onSelectSection!(d.key); }}
            style={chipStyle(on)}>
            <MapIcon size={11} aria-hidden />
            {d.label == null
              ? <Loader2 size={11} className="animate-spin" aria-label={tr('chat.clarification.diagramNameLoading', 'Naming diagram…')} data-testid="diagram-name-loading" />
              : d.label}
            {typeof d.count === 'number' && <span style={{ fontWeight: 500, opacity: 0.75 }}>{d.count}</span>}
          </button>
        );
      })}
      {diagrams.length > 0 && tags.length > 0 && (
        <span aria-hidden style={{ width: 1, alignSelf: 'stretch', margin: '2px 3px', background: DIAGRAM_PALETTE.border }} />
      )}
      {tags.map(t => {
        const id = filterId(t);
        const on = active.has(id);
        const n = counts?.get(id);
        // Would leave nothing with the chips already on: kept in its place
        // (so the row does not jump), muted, and says why.
        const empty = !on && n === 0;
        return (
          <button key={id} type="button" data-testid="filter-chip" data-id={id} data-count={n} aria-pressed={on}
            disabled={empty}
            title={empty ? tr('chat.clarification.chipNoMatch', 'No parts match this with the other filters') : undefined}
            onClick={(e) => { e.stopPropagation(); onToggle(id); }}
            style={{ ...chipStyle(on), ...(empty ? { opacity: 0.45, cursor: 'default' } : {}) }}>
            {on && <Check size={11} strokeWidth={3} aria-hidden />}
            <span data-testid="filter-chip-label">{tagText(t, tr, true)}</span>
            {typeof n === 'number' && <span data-testid="filter-chip-count" style={{ fontWeight: 500, opacity: 0.75 }}>{n}</span>}
          </button>
        );
      })}
      {active.size > 0 && (
        <button type="button" data-testid="filter-clear" onClick={(e) => { e.stopPropagation(); onClear(); }}
          style={{ border: 'none', background: 'transparent', padding: '3px 4px', fontSize: 11.5, fontWeight: 600, color: DIAGRAM_PALETTE.indigo, cursor: 'pointer', fontFamily: 'inherit' }}>
          {tr('chat.clarification.clearFilters', 'Clear filters')}
        </button>
      )}
    </div>
  );
};

export default PartTagRow;
